<template>
  <div class="moderation-tab">
    <!-- Modern Header -->
    <div class="tab-header">
      <div class="header-content">
        <div class="header-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.040A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        </div>
        <div>
          <h2>Logs de Moderação</h2>
          <p>Histórico completo de ações administrativas e auditoria</p>
        </div>
      </div>
      <div class="header-stats">
        <div class="stat-item">
          <span class="stat-value">{{ pagination?.total || 0 }}</span>
          <span class="stat-label">Total</span>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="filters-section">
      <div class="search-bar">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="search-icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Buscar por usuário ou ação..."
          class="search-input"
          @input="debounceSearch"
        />
        <button v-if="filters.search" @click="clearSearch" class="clear-btn">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="filter-groups">
        <div class="filter-group">
          <span class="filter-label">Tipo de Ação:</span>
          <div class="filter-buttons">
            <button
              v-for="actionType in actionTypes"
              :key="actionType.value"
              @click="setActionFilter(actionType.value)"
              :class="[
                'filter-btn',
                { active: filters.actionType === actionType.value },
              ]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path :d="actionType.icon" />
              </svg>
              <span>{{ actionType.label }}</span>
            </button>
          </div>
        </div>

        <div class="filter-group">
          <span class="filter-label">Período:</span>
          <div class="filter-buttons">
            <button
              v-for="period in periods"
              :key="period.value"
              @click="setPeriodFilter(period.value)"
              :class="[
                'filter-btn',
                { active: filters.period === period.value },
              ]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ period.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Logs Timeline -->
    <div class="logs-timeline" v-if="!loading && logs.length > 0">
      <div v-for="log in logs" :key="log.id" class="log-item">
        <div class="log-icon" :class="getLogTypeClass(log.action_type)">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path :d="getLogIcon(log.action_type)" />
          </svg>
        </div>

        <div class="log-content">
          <div class="log-header">
            <div class="log-meta">
              <span class="log-action">{{
                getActionLabel(log.action_type)
              }}</span>
              <span class="log-user">por {{ log.admin_username }}</span>
            </div>
            <span class="log-time">{{
              formatRelativeTime(log.created_at)
            }}</span>
          </div>

          <div class="log-details">
            <div class="log-target">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ log.target_username || "N/A" }}</span>
            </div>

            <p v-if="log.reason" class="log-reason">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ log.reason }}</span>
            </p>

            <div v-if="log.duration_days" class="log-duration">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>Duração: {{ log.duration_days }} dias</span>
            </div>

            <div v-if="log.metadata" class="log-metadata">
              <span class="metadata-label">Detalhes Adicionais:</span>
              <pre class="metadata-content">{{
                formatMetadata(log.metadata)
              }}</pre>
            </div>
          </div>

          <div class="log-footer">
            <span class="log-id">ID: {{ log.id }}</span>
            <span class="log-date">{{ formatFullDate(log.created_at) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && logs.length === 0" class="empty-state">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <h3>Nenhum log encontrado</h3>
      <p>Tente ajustar os filtros de busca</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Carregando logs...</p>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && pagination" class="pagination-section">
      <button
        @click="prevPage"
        :disabled="pagination.page <= 1"
        class="page-btn"
      >
        ← Anterior
      </button>

      <span class="page-info">
        Página {{ pagination.page }} de
        {{ Math.ceil(pagination.total / pagination.limit) }} ({{
          pagination.total
        }}
        logs)
      </span>

      <button
        @click="nextPage"
        :disabled="
          pagination.page >= Math.ceil(pagination.total / pagination.limit)
        "
        class="page-btn"
      >
        Próxima →
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";

export default {
  name: "ModerationTab",
  data() {
    return {
      logs: [],
      loading: false,
      pagination: null,
      filters: {
        search: "",
        actionType: "all",
        period: "all",
      },
      debounceSearch: null,
      actionTypes: [
        {
          value: "all",
          label: "Todos",
          icon: "M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z",
        },
        {
          value: "ban",
          label: "Banimentos",
          icon: "M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z",
        },
        {
          value: "unban",
          label: "Desbans",
          icon: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
        },
        {
          value: "post",
          label: "Posts",
          icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
        },
        {
          value: "comment",
          label: "Comentários",
          icon: "M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z",
        },
      ],
      periods: [
        { value: "all", label: "Todos" },
        { value: "today", label: "Hoje" },
        { value: "week", label: "7 dias" },
        { value: "month", label: "30 dias" },
      ],
    };
  },
  mounted() {
    this.debounceSearch = debounce(this.loadLogs, 500);
    this.loadLogs();
  },
  methods: {
    clearSearch() {
      this.filters.search = "";
      this.loadLogs();
    },

    setActionFilter(type) {
      this.filters.actionType = type;
      this.loadLogs();
    },

    setPeriodFilter(period) {
      this.filters.period = period;
      this.loadLogs();
    },

    async loadLogs() {
      this.loading = true;
      try {
        const params = {
          search: this.filters.search || undefined,
          actionType:
            this.filters.actionType === "all"
              ? undefined
              : this.filters.actionType,
          period:
            this.filters.period === "all" ? undefined : this.filters.period,
          page: this.pagination?.page || 1,
          limit: 20,
        };

        // Simulação de dados - substituir pela chamada real à API
        // const response = await axios.get("/api/admin/moderation/logs", { params });

        // Dados simulados para demonstração
        this.logs = this.generateMockLogs();
        this.pagination = {
          page: 1,
          limit: 20,
          total: 50,
        };
      } catch (error) {
        console.error("Erro ao carregar logs:", error);
        this.$toast?.error("Erro ao carregar logs de moderação");
      } finally {
        this.loading = false;
      }
    },

    generateMockLogs() {
      const actions = [
        "ban",
        "unban",
        "post_delete",
        "post_edit",
        "comment_ban",
      ];
      const users = ["admin", "moderador", "david"];
      const targets = ["user1", "user2", "spammer", "violator"];

      return Array.from({ length: 15 }, (_, i) => ({
        id: i + 1,
        action_type: actions[Math.floor(Math.random() * actions.length)],
        admin_username: users[Math.floor(Math.random() * users.length)],
        target_username: targets[Math.floor(Math.random() * targets.length)],
        reason:
          i % 3 === 0
            ? "Violação dos termos de uso"
            : i % 2 === 0
            ? "Spam repetido"
            : null,
        duration_days: i % 4 === 0 ? 7 : i % 3 === 0 ? 30 : null,
        metadata:
          i % 5 === 0 ? JSON.stringify({ postId: 123, commentId: 456 }) : null,
        created_at: new Date(
          Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000
        ).toISOString(),
      }));
    },

    prevPage() {
      if (this.pagination && this.pagination.page > 1) {
        this.pagination.page--;
        this.loadLogs();
      }
    },

    nextPage() {
      if (
        this.pagination &&
        this.pagination.page <
          Math.ceil(this.pagination.total / this.pagination.limit)
      ) {
        this.pagination.page++;
        this.loadLogs();
      }
    },

    getLogTypeClass(actionType) {
      if (actionType.includes("ban") && !actionType.includes("unban"))
        return "ban";
      if (actionType.includes("unban")) return "unban";
      if (actionType.includes("post")) return "post";
      if (actionType.includes("comment")) return "comment";
      return "default";
    },

    getLogIcon(actionType) {
      if (actionType.includes("ban") && !actionType.includes("unban")) {
        return "M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z";
      }
      if (actionType.includes("unban")) {
        return "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z";
      }
      if (actionType.includes("post")) {
        return "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z";
      }
      if (actionType.includes("comment")) {
        return "M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z";
      }
      return "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.040A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z";
    },

    getActionLabel(actionType) {
      const labels = {
        ban: "Usuário Banido",
        unban: "Usuário Desbanido",
        comment_ban: "Comentários Restritos",
        comment_unban: "Comentários Liberados",
        post_delete: "Post Deletado",
        post_edit: "Post Editado",
        post_publish: "Post Publicado",
        post_unpublish: "Post Ocultado",
      };
      return labels[actionType] || actionType;
    },

    formatRelativeTime(date) {
      const now = new Date();
      const past = new Date(date);
      const diffMs = now - past;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Agora mesmo";
      if (diffMins < 60) return `${diffMins}m atrás`;
      if (diffHours < 24) return `${diffHours}h atrás`;
      if (diffDays < 7) return `${diffDays}d atrás`;
      return past.toLocaleDateString("pt-BR");
    },

    formatFullDate(date) {
      return new Date(date).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatMetadata(metadata) {
      try {
        return JSON.stringify(JSON.parse(metadata), null, 2);
      } catch {
        return metadata;
      }
    },
  },
};
</script>

<style scoped>
/* Modern Moderation Tab Styles */
.moderation-tab {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.tab-header {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.header-icon svg {
  width: 32px;
  height: 32px;
  color: white;
}

.tab-header h2 {
  font-size: 1.875rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.tab-header p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.header-stats {
  display: flex;
  gap: 2rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: #ef4444;
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
}

/* Filters Section */
.filters-section {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
}

.search-bar {
  position: relative;
  margin-bottom: 1.5rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #9ca3af;
}

.search-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.9375rem;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.search-input:focus {
  outline: none;
  border-color: #ef4444;
  background: white;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.clear-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.clear-btn svg {
  width: 16px;
  height: 16px;
}

.filter-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  min-width: 120px;
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-btn svg {
  width: 16px;
  height: 16px;
}

.filter-btn:hover {
  background: white;
  border-color: #d1d5db;
  color: #374151;
}

.filter-btn.active {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-color: #ef4444;
  color: white;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

/* Logs Timeline */
.logs-timeline {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.log-item {
  background: white;
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 2px solid #e5e7eb;
  display: flex;
  gap: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideIn 0.4s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.log-item:hover {
  transform: translateX(4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  border-color: #ef4444;
}

.log-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.log-icon svg {
  width: 24px;
  height: 24px;
  color: white;
}

.log-icon.ban {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.log-icon.unban {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.log-icon.post {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.log-icon.comment {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.log-icon.default {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.log-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.log-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.log-action {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
}

.log-user {
  font-size: 0.8125rem;
  color: #6b7280;
}

.log-time {
  font-size: 0.8125rem;
  color: #9ca3af;
  font-weight: 500;
}

.log-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.log-target,
.log-reason,
.log-duration {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: #374151;
}

.log-target svg,
.log-reason svg,
.log-duration svg {
  width: 16px;
  height: 16px;
  color: #6b7280;
  flex-shrink: 0;
}

.log-metadata {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.metadata-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.metadata-content {
  font-family: "Courier New", monospace;
  font-size: 0.8125rem;
  color: #374151;
  margin: 0;
  overflow-x: auto;
  white-space: pre;
}

.log-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
  font-size: 0.75rem;
  color: #9ca3af;
}

/* Empty State */
.empty-state {
  background: white;
  border-radius: 16px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 2px dashed #e5e7eb;
}

.empty-state svg {
  width: 64px;
  height: 64px;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  font-size: 0.9375rem;
  color: #9ca3af;
  margin: 0;
}

/* Loading State */
.loading-state {
  background: white;
  border-radius: 16px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #ef4444;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
}

.page-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.page-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.page-info {
  font-size: 0.9375rem;
  color: #6b7280;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .tab-header {
    flex-direction: column;
    gap: 1.5rem;
  }

  .header-content,
  .header-stats {
    width: 100%;
    justify-content: center;
  }

  .filter-groups {
    flex-direction: column;
  }

  .filter-group {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-label {
    min-width: auto;
  }

  .log-item {
    flex-direction: column;
  }

  .pagination-section {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
